// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { Layers, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FilterMultiSelect } from "@/components/shared/filter-multi-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { cn } from "@/lib/utils";
import {
  formatTime,
  getCreationInfo,
  getMatchingRules,
  getWorkabilityInfo,
  minutesToPercent,
  timeToMinutes,
  W3_IMMUNE_TYPES,
} from "./octo-utils";
import { SAMPLE_DATA } from "./sample-data";
import { displayType, TASK_COLORS } from "./task-colors";
import type { GanttRow, LayerType, OctoChainData, RuleTooltipData } from "./types";

const FILTER_DEFAULTS: {
  chain: string;
  store: string;
  layers: string[];
  depts: string[];
  types: string[];
} = {
  chain: "",
  store: "",
  layers: ["creation"],
  depts: [],
  types: [],
};

interface OctoVisualizerProps {
  initialFilters?: string;
}

export function OctoVisualizer({ initialFilters }: OctoVisualizerProps) {
  const navigate = useNavigate();
  const { data: ctx, isLoading: ctxLoading } = useBackofficeContext();

  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [selectedChain, setSelectedChain] = useState(initial.chain);
  const [selectedStore, setSelectedStore] = useState(initial.store);
  const [activeLayers, setActiveLayers] = useState<Set<LayerType>>(new Set(initial.layers) as Set<LayerType>);
  const [selectedDepts, setSelectedDepts] = useState<Set<string> | null>(
    initial.depts.length > 0 ? new Set(initial.depts) : null,
  );
  const [selectedTypes, setSelectedTypes] = useState<Set<string> | null>(
    initial.types.length > 0 ? new Set(initial.types) : null,
  );
  const [nowPct, setNowPct] = useState<number | null>(null);

  const chainOptions = useMemo(
    () => (ctx?.chains ?? []).map((c) => ({ value: c.value, label: c.display })),
    [ctx?.chains],
  );

  const storeOptions = useMemo(
    () => (ctx?.stores ?? []).map((s) => ({ value: s.value, label: s.display })),
    [ctx?.stores],
  );

  // TODO: replace with API call once available
  const data: OctoChainData | null = selectedChain === SAMPLE_DATA.chain ? SAMPLE_DATA : null;

  const openMin = data ? timeToMinutes(data.store_hours.open) || 0 : 0;
  const closeMin = data ? timeToMinutes(data.store_hours.close) || 1440 : 1440;

  // Update now line
  useEffect(
    function updateCurrentTimeIndicator() {
      function update() {
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        const pct = minutesToPercent(nowMin, openMin, closeMin);
        setNowPct(pct >= 0 && pct <= 100 ? pct : null);
      }
      update();
      const id = setInterval(update, 60000);
      return () => clearInterval(id);
    },
    [openMin, closeMin],
  );

  // Compute visible rows
  // null = all (no filter), Set([...]) = specific items, Set([]) = nothing
  const rows = useMemo<GanttRow[]>(() => {
    if (!data) return [];
    const depts = selectedDepts ? data.departments.filter((d) => selectedDepts.has(d.code)) : data.departments;
    const types = selectedTypes ? data.task_types.filter((t) => selectedTypes.has(t)) : data.task_types;
    const result: GanttRow[] = [];
    for (const dept of depts) {
      for (const taskType of types) {
        result.push({ dept, taskType });
      }
    }
    return result;
  }, [data, selectedDepts, selectedTypes]);

  // Hour labels
  const hourLabels = useMemo(() => {
    const hourStart = Math.floor(openMin / 60);
    const hourEnd = closeMin === 0 || closeMin === 1440 ? 24 : Math.ceil(closeMin / 60);
    const labels: { hour: number; pct: number }[] = [];
    for (let h = hourStart; h < hourEnd; h += 2) {
      const labelMin = (h % 24) * 60;
      labels.push({ hour: h, pct: minutesToPercent(labelMin, openMin, closeMin) });
    }
    return labels;
  }, [openMin, closeMin]);

  // Hour grid lines
  const gridLines = useMemo(() => {
    const hourStart = Math.floor(openMin / 60);
    const hourEnd = closeMin === 0 || closeMin === 1440 ? 24 : Math.ceil(closeMin / 60);
    const lines: number[] = [];
    for (let h = hourStart; h <= hourEnd; h++) {
      lines.push(minutesToPercent(h * 60, openMin, closeMin));
    }
    return lines;
  }, [openMin, closeMin]);

  // Slot zones
  const slotZones = useMemo(
    () => [
      { name: "Morning", start: openMin, end: 720, variant: "morning" as const },
      { name: "Afternoon", start: 720, end: 1080, variant: "afternoon" as const },
      { name: "Evening", start: 1080, end: closeMin || 1440, variant: "evening" as const },
    ],
    [openMin, closeMin],
  );

  // Layer toggle handler
  const toggleLayer = useCallback((layer: LayerType) => {
    setActiveLayers((prev) => {
      if (layer === "creation") return new Set(["creation"]);
      const next = new Set(prev);
      next.add("creation");
      if (next.has(layer)) next.delete(layer);
      else next.add(layer);
      if (next.size === 1 && next.has("creation")) return new Set(["creation"]);
      return next;
    });
  }, []);

  // Stats
  const stats = useMemo(() => {
    if (!data) return { total: 0, visibility: 0, creation: 0, workability: 0, rows: 0 };
    const vis = data.rules.filter((r) => r.layer === "visibility").length;
    const crt = data.rules.filter((r) => r.layer === "creation").length;
    const wrk = data.rules.filter((r) => r.layer === "workability").length;
    return { total: data.rules.length, visibility: vis, creation: crt, workability: wrk, rows: rows.length };
  }, [data, rows.length]);

  const isFiltered =
    selectedChain !== "" ||
    selectedStore !== "" ||
    selectedDepts !== null ||
    selectedTypes !== null ||
    activeLayers.size > 1;

  // Sync filter state to URL
  const syncUrl = useCallback(() => {
    const current = {
      chain: selectedChain,
      store: selectedStore,
      layers: [...activeLayers],
      depts: selectedDepts ? [...selectedDepts] : [],
      types: selectedTypes ? [...selectedTypes] : [],
    };
    const encoded = encodeFilters(current, FILTER_DEFAULTS);
    navigate({
      to: "/octo/visualizer",
      search: encoded ? { f: encoded } : {},
      replace: true,
    });
  }, [selectedChain, selectedStore, activeLayers, selectedDepts, selectedTypes, navigate]);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  return (
    <div className="space-y-4">
      {/* Toolbar — matches DataTable layout */}
      <div className="flex items-center gap-2">
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
          <VirtualSelectPopover
            value={selectedChain}
            onChange={setSelectedChain}
            options={chainOptions}
            placeholder="Select Chain..."
            searchPlaceholder="Search chains..."
            emptyText="No chains found"
            className="w-56"
            loading={ctxLoading}
          />
          {selectedChain && (
            <VirtualSelectPopover
              value={selectedStore}
              onChange={setSelectedStore}
              options={storeOptions}
              placeholder="Select Store..."
              searchPlaceholder="Search stores..."
              emptyText="No stores found"
              className="w-56"
              loading={ctxLoading}
            />
          )}
          <FilterMultiSelect
            label="Department"
            values={selectedDepts}
            onChange={setSelectedDepts}
            options={data?.departments.map((d) => ({ value: d.code, label: `${d.code} ${d.name}` })) ?? []}
            searchPlaceholder="Search departments..."
            emptyText="No departments found"
            disabled={!data}
          />
          <FilterMultiSelect
            label="Task Type"
            values={selectedTypes}
            onChange={setSelectedTypes}
            options={data?.task_types.map((t) => ({ value: t, label: displayType(t) })) ?? []}
            searchPlaceholder="Search task types..."
            emptyText="No task types found"
            disabled={!data}
          />
          <FilterToggle
            active={activeLayers.has("workability")}
            onChange={() => toggleLayer("workability")}
            icon={Layers}
            label="Workability"
            disabled={!data}
          />
          <FilterToggle
            active={activeLayers.has("visibility")}
            onChange={() => toggleLayer("visibility")}
            icon={Layers}
            label="Visibility"
            disabled={!data}
          />
          {isFiltered && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSelectedChain("");
                setSelectedStore("");
                setSelectedDepts(null);
                setSelectedTypes(null);
                setActiveLayers(new Set(["creation"]));
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {!data ? (
        <div className="rounded-lg border bg-card flex items-center justify-center py-20 text-muted-foreground text-sm">
          No OCTO data available for the selected chain. Select a supported chain to view the visualizer.
        </div>
      ) : (
        <>
          {/* Gantt Chart */}
          <div className="rounded-lg border overflow-hidden bg-card">
            {/* Grid layout: type | bars */}
            <div className="grid" style={{ gridTemplateColumns: "200px 1fr" }}>
              {/* Header */}
              <div className="bg-sidebar h-10 px-4 flex items-center text-sm font-medium text-foreground border-b">
                Type
              </div>
              <div className="bg-sidebar border-b relative">
                <div className="relative w-full h-full">
                  {hourLabels.map((l) => (
                    <span
                      key={l.hour}
                      className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] font-mono text-muted-foreground/60"
                      style={{ left: `${l.pct}%` }}
                    >
                      {`${l.hour === 24 ? "00" : String(l.hour).padStart(2, "0")}:00`}
                    </span>
                  ))}
                </div>
              </div>

              {/* Slot zone row */}
              <div className="bg-muted/30 border-b" />
              <div className="relative h-6 bg-muted/30 border-b">
                {slotZones.map((s) => {
                  const left = minutesToPercent(s.start, openMin, closeMin);
                  const right = minutesToPercent(s.end, openMin, closeMin);
                  return (
                    <div
                      key={s.name}
                      className={cn(
                        "absolute top-1 h-4 rounded-sm flex items-center justify-center text-[9px] font-mono font-medium uppercase tracking-wide",
                        slotVariant(s.variant),
                      )}
                      style={{ left: `${left}%`, width: `${right - left}%` }}
                    >
                      {s.name}
                    </div>
                  );
                })}
                {nowPct !== null && (
                  <span
                    className="absolute -top-0.5 text-[8px] font-mono font-bold text-destructive -translate-x-1/2 z-20"
                    style={{ left: `${nowPct}%` }}
                  >
                    NOW
                  </span>
                )}
              </div>

              {/* Data rows */}
              {rows.length === 0 && (
                <div className="col-span-2 py-10 text-center text-sm text-muted-foreground font-mono">
                  No matching rules for current filters
                </div>
              )}
              {rows.map((row, i) => {
                const isNewDept = i === 0 || rows[i - 1]!.dept.code !== row.dept.code;
                return (
                  <React.Fragment key={`${row.dept.code}-${row.taskType}`}>
                    {isNewDept && (
                      <div className="col-span-2 grid grid-cols-subgrid">
                        <div className="px-4 py-2 bg-muted/40 border-b text-sm font-medium flex items-center gap-2 whitespace-nowrap">
                          <span className="text-muted-foreground">{row.dept.code}</span>
                          {row.dept.name}
                        </div>
                        <div className="relative bg-muted/40 border-b">
                          {nowPct !== null && (
                            <div
                              className="absolute top-0 bottom-0 w-px bg-destructive z-10"
                              style={{ left: `${nowPct}%` }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    <GanttRowComponent
                      data={data}
                      row={row}
                      isLast={i === rows.length - 1}
                      activeLayers={activeLayers}
                      openMin={openMin}
                      closeMin={closeMin}
                      gridLines={gridLines}
                      nowPct={nowPct}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-6 text-[10px] font-mono text-muted-foreground border-t pt-3">
            <span>
              Rules: <span className="font-semibold text-foreground">{stats.total}</span>
            </span>
            <span>
              Visibility: <span className="font-semibold text-foreground">{stats.visibility}</span>
            </span>
            <span>
              Creation: <span className="font-semibold text-foreground">{stats.creation}</span>
            </span>
            <span>
              Workability: <span className="font-semibold text-foreground">{stats.workability}</span>
            </span>
            <span>
              Showing: <span className="font-semibold text-foreground">{stats.rows} rows</span>
            </span>
            <span>
              Updated: <span className="font-semibold text-foreground">{data.last_updated}</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Slot zone variant styles ─────────────────────────────────────────────────

function slotVariant(variant: "morning" | "afternoon" | "evening") {
  switch (variant) {
    case "morning":
      return "bg-sky-500/10 border border-sky-500/20 text-sky-600/60 dark:text-sky-400/50";
    case "afternoon":
      return "bg-amber-500/10 border border-amber-500/20 text-amber-600/60 dark:text-amber-400/50";
    case "evening":
      return "bg-purple-500/10 border border-purple-500/20 text-purple-600/60 dark:text-purple-400/50";
  }
}

// ── Gantt Row ────────────────────────────────────────────────────────────────

interface GanttRowProps {
  data: OctoChainData;
  row: GanttRow;
  isLast: boolean;
  activeLayers: Set<LayerType>;
  openMin: number;
  closeMin: number;
  gridLines: number[];
  nowPct: number | null;
}

function GanttRowComponent({ data, row, isLast, activeLayers, openMin, closeMin, gridLines, nowPct }: GanttRowProps) {
  const color = TASK_COLORS[row.taskType] || "#666";
  const borderClass = isLast ? "" : "border-b";

  const creationInfo = getCreationInfo(data, row.dept.code, row.taskType);
  const rules = getMatchingRules(data, row.dept.code, row.taskType);
  const wRules = getWorkabilityInfo(data, row.dept.code, row.taskType);
  const isWorkabilityImmune = W3_IMMUNE_TYPES.has(row.taskType);
  const avExclude = data.always_visible_exclude?.[row.taskType] || [];
  const isAlwaysVisible = data.always_visible.includes(row.taskType) && !avExclude.includes(row.dept.code);

  const layerOrder: Record<string, number> = { workability: 0, creation: 1, visibility: 2 };
  const sortedRules = [...rules].sort((a, b) => (layerOrder[a.layer] || 0) - (layerOrder[b.layer] || 0));

  return (
    <TooltipProvider delayDuration={100}>
      <div className="col-span-2 grid grid-cols-subgrid group/row">
        {/* Type cell */}
        <div
          className={cn(
            "px-2 py-1.5 text-sm font-medium flex items-center gap-1.5 bg-card transition-colors group-hover/row:bg-muted/50",
            borderClass,
          )}
        >
          <span
            className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-normal border"
            style={{ background: `${color}30`, color, borderColor: `${color}60` }}
          >
            {displayType(row.taskType)}
          </span>
          {wRules.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="ml-auto text-[8px] font-medium text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/10 px-1 py-0 h-4 cursor-default"
                >
                  W
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[300px] font-mono text-xs space-y-1">
                <p className="font-semibold text-primary">Workability Rules</p>
                {wRules.map((r) => (
                  <div key={r.id}>
                    <span className="font-semibold text-primary">{r.id}</span>
                    {r.condition && <div className="text-destructive">{r.condition}</div>}
                    {r.note && <div className="text-muted-foreground text-[10px]">{r.note}</div>}
                  </div>
                ))}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Bar cell */}
        <div className={cn("relative bg-card transition-colors group-hover/row:bg-muted/50", borderClass)}>
          <div className="relative h-11">
            {/* Hour grid lines */}
            {gridLines.map((pct) => (
              <div
                key={pct}
                className="absolute top-0 bottom-0 w-px bg-border/50 pointer-events-none z-[1]"
                style={{ left: `${pct}%` }}
              />
            ))}

            {/* Now line */}
            {nowPct !== null && (
              <div
                className="absolute top-0 bottom-0 w-px bg-destructive z-10 pointer-events-none"
                style={{ left: `${nowPct}%` }}
              />
            )}

            {/* Always visible bar */}
            {isAlwaysVisible && activeLayers.has("visibility") && (
              <TimeBar
                left={0}
                width={100}
                color={color}
                opacity={creationInfo.allowed ? 0.75 : 0.25}
                layer="visibility"
                label="Always visible"
                tooltip={{
                  id: "always",
                  layer: "visibility",
                  note: "Always visible — no time restriction",
                }}
              />
            )}

            {/* Creation baseline (dashed) */}
            {creationInfo.allowed &&
              (creationInfo.windows.length > 0 ? (
                creationInfo.windows.map((win) => {
                  const startMin = timeToMinutes(win.start);
                  const endMin = timeToMinutes(win.end);
                  if (startMin === null || endMin === null) return null;
                  const left = minutesToPercent(startMin, openMin, closeMin);
                  const right = minutesToPercent(endMin, openMin, closeMin);
                  return (
                    <TimeBar
                      key={`creation-${win.start}`}
                      left={left}
                      width={right - left}
                      color={color}
                      variant="creation-baseline"
                      tooltip={{
                        id: creationInfo.ruleId || "default",
                        layer: "creation",
                        note: creationInfo.note || "Can be created (time-windowed)",
                      }}
                    />
                  );
                })
              ) : (
                <TimeBar
                  left={0}
                  width={100}
                  color={color}
                  variant="creation-baseline"
                  tooltip={{
                    id: "default",
                    layer: "creation",
                    note: "Can be created in this department",
                  }}
                />
              ))}

            {/* Immune label for workability-immune types */}
            {isWorkabilityImmune && activeLayers.has("workability") && (
              <TimeBar
                left={0}
                width={100}
                color="transparent"
                variant="immune"
                label="IMMUNE"
                tooltip={{
                  id: "IMMUNE",
                  layer: "workability",
                  note: `${displayType(row.taskType)} is immune to workability rules`,
                }}
              />
            )}

            {/* Rule bars */}
            {sortedRules.map((rule) => {
              if (!activeLayers.has(rule.layer)) return null;
              if (rule.blocked || rule.blocked_outside_window) return null;
              if (!rule.windows?.length) return null;
              if (rule.layer === "workability" && isWorkabilityImmune) return null;

              return rule.windows.map((win) => {
                const startMin = timeToMinutes(win.start);
                const endMin = timeToMinutes(win.end);
                if (startMin === null || endMin === null) return null;

                const left = minutesToPercent(startMin, openMin, closeMin);
                const right = minutesToPercent(endMin, openMin, closeMin);
                const width = right - left;
                const dimmed = !creationInfo.allowed && rule.layer === "visibility";
                const opacity = dimmed
                  ? 0.25
                  : rule.layer === "creation"
                    ? 1.0
                    : rule.layer === "workability"
                      ? 0.25
                      : 0.75;

                return (
                  <TimeBar
                    key={`${rule.id}-${win.start}`}
                    left={left}
                    width={width}
                    color={color}
                    opacity={opacity}
                    layer={rule.layer}
                    label={
                      rule.layer !== "workability" && width > 5
                        ? `${formatTime(win.start)}-${formatTime(win.end)}`
                        : undefined
                    }
                    tooltip={{
                      id: rule.id,
                      layer: rule.layer,
                      window: win,
                      note: rule.note,
                      condition: rule.condition,
                    }}
                  />
                );
              });
            })}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

// ── Time Bar ─────────────────────────────────────────────────────────────────

interface TimeBarProps {
  left: number;
  width: number;
  color: string;
  opacity?: number;
  layer?: string;
  variant?: "creation-baseline" | "immune";
  label?: string;
  tooltip: RuleTooltipData;
}

function TimeBar({ left, width, color, opacity = 1, layer, variant, label, tooltip }: TimeBarProps) {
  const layerPattern =
    layer === "workability" && variant !== "immune"
      ? "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.25) 3px, rgba(0,0,0,0.25) 6px)"
      : layer === "visibility" && variant !== "immune"
        ? "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)"
        : undefined;

  const zIndex = layer === "visibility" ? 4 : layer === "workability" ? 3 : 2;

  const style: React.CSSProperties = {
    left: `${left}%`,
    width: `${width}%`,
    opacity,
    zIndex,
  };

  if (variant === "creation-baseline") {
    style.borderColor = color;
    style.color = color;
  } else if (variant === "immune") {
    // immune styling handled by className
  } else {
    style.backgroundColor = color;
    if (layerPattern) {
      style.backgroundImage = layerPattern;
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "absolute top-1 bottom-1 rounded-sm flex items-center justify-center font-mono text-[9px] font-medium tracking-wide overflow-hidden min-w-0.5",
            variant === "creation-baseline" && "border-2 border-dashed bg-transparent opacity-55",
            variant === "immune" && "border border-dashed border-muted-foreground/40 opacity-60",
            layer === "workability" && variant !== "immune" && "pointer-events-none",
          )}
          style={style}
        >
          {label && (
            <span
              className={cn(
                "whitespace-nowrap px-1.5",
                variant === "immune"
                  ? "text-muted-foreground text-[9px] font-semibold tracking-widest"
                  : "text-white drop-shadow-sm",
              )}
            >
              {label}
            </span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[320px] font-mono text-xs space-y-1">
        <div className="font-semibold text-primary">{tooltip.id}</div>
        <div>Layer: {tooltip.layer}</div>
        {tooltip.window && tooltip.layer !== "workability" && (
          <div className="text-amber-500">
            {tooltip.window.start} - {tooltip.window.end}
          </div>
        )}
        {tooltip.condition && <div className="text-destructive">{tooltip.condition}</div>}
        {tooltip.note && <div className="text-muted-foreground text-[10px]">{tooltip.note}</div>}
      </TooltipContent>
    </Tooltip>
  );
}
